import { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import SmokerSelect from './steps/SmokerSelect';
import MeatSelect from './steps/MeatSelect';
import WeightInput from './steps/WeightInput';
import CookSettings from './steps/CookSettings';
import LocationInput from './steps/LocationInput';
import Results from './steps/Results';
import type { CookDetails, MeatProperties } from '../types';

const STEPS = [
  { id: 'smoker', title: 'Smoker', component: SmokerSelect },
  { id: 'meat', title: 'Meat', component: MeatSelect },
  { id: 'weights', title: 'Weights', component: WeightInput },
  { id: 'settings', title: 'Settings', component: CookSettings },
  { id: 'location', title: 'Location', component: LocationInput },
  { id: 'results', title: 'Results', component: Results },
] as const;

export default function CookPlanner() {
  const [currentStep, setCurrentStep] = useState(0);

  // Stamp each wizard step into browser history so the back button works in-app
  useEffect(() => {
    // Mark the initial page as step 0 (replaceState, not push, so we don't add an extra entry)
    window.history.replaceState({ step: 0 }, '');

    const handlePopState = (event: PopStateEvent) => {
      const step = (event.state as { step?: number } | null)?.step ?? 0;
      setCurrentStep(step);
      window.scrollTo({ top: 0, behavior: 'instant' });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const [formData, setFormData] = useState<Partial<CookDetails>>({
    pieces: [{ id: 'A', weight: 0 }],
    smokerTempF: 225,
    restHours: 1,
    spacing: {
      arrangement: 'gaps',
      spacingFactor: 1.0,
    },
    wrapping: {
      enabled: false,
      wrapType: 'foil',
      atTempF: 160,
    },
    spritz: {
      enabled: false,
      totalOpenings: undefined,
    },
    pan: {
      enabled: false,
      type: 'aluminum-disposable',
      elevated: false,
    },
    exposure: { 
      useWeatherTemp: true, 
      useWeatherWind: true, 
      useWeatherPrecipitation: true 
    },
  });

  const CurrentStepComponent = STEPS[currentStep].component;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === STEPS.length - 1;
  const hasValidWeights = (formData.pieces || []).some((piece) => piece.weight > 0);

  const handleNext = () => {
    if (currentStep === 2 && !hasValidWeights) {
      return;
    }
    if (currentStep < STEPS.length - 1) {
      const nextStep = currentStep + 1;
      window.history.pushState({ step: nextStep }, '');
      setCurrentStep(nextStep);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      // Trigger the browser's back — the popstate listener will update currentStep
      window.history.back();
    }
  };

  const [meatConfigs, setMeatConfigs] = useState<Record<string, MeatProperties>>({});

  // Load meat configs
  useEffect(() => {
    fetch('/config/meats.json')
      .then(res => res.json())
      .then((meats: MeatProperties[]) => {
        const configMap = meats.reduce((acc, meat) => {
          acc[meat.type] = meat;
          return acc;
        }, {} as Record<string, MeatProperties>);
        setMeatConfigs(configMap);
      })
      .catch(err => console.error('Failed to load meat configs:', err));
  }, []);

  const updateFormData = (data: Partial<CookDetails>) => {
    setFormData((prev) => {
      const updated = { ...prev, ...data };
      
      // Auto-adjust smoker temp for meats with recommended temp
      if (data.meatType && prev.meatType !== data.meatType) {
        const meatConfig = meatConfigs[data.meatType];
        const recommendedTemp = meatConfig?.empirical?.recommendedSmokerTempF;
        if (recommendedTemp) {
          updated.smokerTempF = recommendedTemp;
        }
      }
      
      return updated;
    });
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress Bar */}
      <div className="mb-6 sm:mb-8">
        <div className="flex justify-between mb-2">
          {STEPS.map((step, index) => (
            <button
              key={step.id}
              onClick={() => setCurrentStep(index)}
              className={`flex-1 text-xs sm:text-sm font-medium transition-colors ${
                index === currentStep
                  ? 'text-flame-600'
                  : index < currentStep
                  ? 'text-smoke-600'
                  : 'text-smoke-400'
              }`}
              disabled={index > currentStep}
            >
              <span className="hidden sm:inline">{step.title}</span>
              <span className="sm:hidden">{index + 1}</span>
            </button>
          ))}
        </div>
        <div className="h-2 bg-smoke-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-flame-600 transition-all duration-300 ease-out"
            style={{ width: `${((currentStep + 1) / STEPS.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Step Content */}
      <div className="card min-h-[400px] sm:min-h-[500px]">
        <h2 className="text-2xl sm:text-3xl font-bold text-smoke-900 mb-2">
          {STEPS[currentStep].title}
        </h2>
        <p className="text-smoke-600 mb-6 text-sm sm:text-base">
          {currentStep === 0 && 'Select your smoker model'}
          {currentStep === 1 && 'Choose the meat type'}
          {currentStep === 2 && 'Enter trimmed weights for each piece'}
          {currentStep === 3 && 'Configure temperature and cook strategy'}
          {currentStep === 4 && 'Enter your location for weather data'}
          {currentStep === 5 && 'Your personalized cook plan'}
        </p>

        <div className="mb-8">
          <CurrentStepComponent
            data={formData}
            onChange={updateFormData}
            onNext={handleNext}
          />
          {currentStep === 2 && !hasValidWeights && (
            <p className="mt-4 text-sm text-flame-700">
              Enter at least one piece weight to continue.
            </p>
          )}
        </div>

        {/* Navigation Buttons */}
        {!isLastStep && (
          <div className="flex gap-3 pt-6 border-t border-smoke-200">
            <button
              onClick={handleBack}
              disabled={isFirstStep}
              className="btn-secondary flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
            <button
              onClick={handleNext}
              disabled={
                (currentStep === 2 && !hasValidWeights) ||
                (currentStep === 4 && !formData.location)
              }
              className="btn-primary flex-1 flex items-center justify-center gap-2"
            >
              <span>{currentStep === STEPS.length - 2 ? 'Calculate' : 'Continue'}</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Mobile Helper Text */}
      <div className="mt-4 text-center text-sm text-smoke-600">
        <p className="sm:hidden">Step {currentStep + 1} of {STEPS.length}</p>
      </div>
    </div>
  );
}
